import React, { useState, useMemo } from 'react';
import {
  Box as MuiBox,
  Button as MuiButton,
  TextField as MuiTextField,
  Typography as MuiTypography,
} from '@mui/material';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { Form, FormSpy } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import QuestionnaireDialogItem from './QuestionnaireDialogItem';
import EditableQuestionnaireItem from './EditableQuestionnaireItem';
import useUpdateQuestionnaireResponse from '../../hooks/useUpdateQuestionnaireResponse';
import useDialog from 'lib/hooks/useDialog';
import ConfirmationDialog from 'components/Dialogs/ConfirmationDialog';
import { prepareQuestionResponseData } from 'components/questionnaire/utils/prepareData';

interface QuestionnaireAmendmentStepProps {
  questionnaire: any;
  responses: {
    id: string;
    questionResponses: any[];
    questionStates: any[];
  };
  metricDefinitionId: string;
  onCancel: () => void;
  onSubmitSuccess: () => void;
}

const QuestionnaireAmendmentStep: React.FC<QuestionnaireAmendmentStepProps> = ({
  questionnaire,
  responses,
  metricDefinitionId,
  onCancel,
  onSubmitSuccess,
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const confirmationDialog = useDialog();
  const {
    updateQuestionnaireResponse,
    loading,
  } = useUpdateQuestionnaireResponse();

  const [reasonForAmendment, setReasonForAmendment] = useState('');
  const [changedFields, setChangedFields] = useState<Set<number>>(new Set());

  // Compute initial values for form using array pattern answer[index]
  const initialValues = useMemo(() => {
    if (
      !responses?.questionResponses?.length ||
      !questionnaire?.questions?.length
    ) {
      return { answer: [] };
    }

    const answers = new Array(questionnaire.questions.length);

    responses.questionResponses.forEach((response) => {
      const questionIndex = questionnaire.questions.findIndex(
        (q: any) => q.id === response.questionId
      );

      if (questionIndex !== -1) {
        const question = questionnaire.questions[questionIndex];

        // Extract value based on question type and response structure
        if (question?.questionType === 'QUESTION_DATE') {
          // Handle date questions - check the mode to determine structure
          if (response.valueCognitiveTest) {
            // For cognitive test mode, transform the data to match form expectations
            const cognitiveData = response.valueCognitiveTest;
            answers[questionIndex] = {
              // Year needs to be a Date object for the DatePicker
              year: cognitiveData.year.toString(),
              // Month, day, and dayOfWeek can remain as primitives
              // FormAutocomplete will match them using getLabelByValue
              month: cognitiveData.month,
              day: cognitiveData.day,
              dayOfWeek: cognitiveData.dayOfWeek.toLowerCase(),
            };
          } else {
            // For default mode, use datetimeValue or value
            answers[questionIndex] = response.datetimeValue || response.value;
          }
        } else if (response.listOfAnswerOptionValue) {
          answers[questionIndex] = response.listOfAnswerOptionValue.map(
            (opt: any) => opt.id
          );
        } else if (response.value !== undefined) {
          answers[questionIndex] = response.value;
        } else if (response.decimalValue !== undefined) {
          answers[questionIndex] = response.decimalValue;
        } else if (response.intValue !== undefined) {
          answers[questionIndex] = response.intValue;
        } else if (response.datetimeValue) {
          answers[questionIndex] = response.datetimeValue;
        } else if (response.answers) {
          answers[questionIndex] = response.answers;
        } else if (response.valueCognitiveTest) {
          answers[questionIndex] = response.valueCognitiveTest;
        }
      }
    });

    return { answer: answers };
  }, [responses, questionnaire]);

  const handleSubmit = async (values: any) => {
    // Validation
    if (!reasonForAmendment.trim()) {
      enqueueSnackbar(t('Please provide a reason for the amendment'), {
        variant: 'error',
      });
      return;
    }

    if (changedFields.size === 0) {
      enqueueSnackbar(t('Please make at least one change before submitting'), {
        variant: 'error',
      });
      return;
    }

    // Show confirmation dialog
    const result = await confirmationDialog.open({
      data: {
        cancelText: t('Cancel'),
        confirmText: t('Confirm Update'),
        description: t(
          'Are you sure you want to update this questionnaire response? This action cannot be undone.'
        ),
      },
    });

    if (!result.isConfirmed) {
      return;
    }

    try {
      // Prepare question responses using the utility function
      const questionResponses = Array.from(changedFields).map(
        (questionIndex) => {
          const question = questionnaire.questions?.[questionIndex];
          const value = values.answer?.[questionIndex];

          return {
            questionId: question.id,
            ...prepareQuestionResponseData(question, value),
          };
        }
      );

      // Submit the mutation
      const result = await updateQuestionnaireResponse({
        variables: {
          record: {
            questionnaireResponseId: responses.id,
            questionResponsesJson: JSON.stringify(questionResponses),
            reasonForAmendment: reasonForAmendment.trim(),
          },
        },
      });

      if (result.data?.questionnaire?.updateQuestionnaireResponse?.ok) {
        enqueueSnackbar(t('Questionnaire response updated successfully'), {
          variant: 'success',
        });
        onSubmitSuccess();
      } else {
        const errorMessage =
          result.data?.questionnaire?.updateQuestionnaireResponse?.error
            ?.message || t('An error occurred');
        enqueueSnackbar(errorMessage, { variant: 'error' });
      }
    } catch (error) {
      console.error('Error updating questionnaire response:', error);
      const errorMessage =
        error instanceof Error ? error.message : t('An error occurred');
      enqueueSnackbar(errorMessage, {
        variant: 'error',
      });
    }
  };

  return (
    <>
      <SHeader>
        <MuiTypography variant="h4" align="center">
          {t('Update Answers')}
        </MuiTypography>
      </SHeader>

      <SContent>
        <Form
          onSubmit={handleSubmit}
          mutators={{ ...arrayMutators }}
          initialValues={initialValues}
          render={({ values, form, handleSubmit }) => (
            <form onSubmit={handleSubmit}>
              <SComparisonGrid>
                {/* Left Column - Current Version (Read-only) */}
                <SColumn>
                  <SColumnHeader>
                    <MuiTypography variant="h6" fontWeight={600}>
                      {t('Current Version')}
                    </MuiTypography>
                  </SColumnHeader>
                  <SColumnContent>
                    {responses?.questionStates?.map((item, i) => {
                      const response = responses.questionResponses?.find(
                        (q) => q.questionId === item.questionId
                      );

                      const question = questionnaire.questions?.find(
                        (q: any) => q.id === item.questionId
                      );

                      return (
                        <QuestionnaireDialogItem
                          key={item.questionId}
                          state={item}
                          response={response}
                          question={question}
                          index={i}
                          showAmended={false}
                        />
                      );
                    })}
                  </SColumnContent>
                </SColumn>

                {/* Right Column - New Amendment (Editable) */}
                <SColumn>
                  <SColumnHeader>
                    <MuiTypography variant="h6" fontWeight={600}>
                      {t('New Amendment')}
                    </MuiTypography>
                  </SColumnHeader>
                  <SColumnContent>
                    <FormSpy
                      subscription={{ values: true, initialValues: true }}
                      onChange={(formState) => {
                        // Track which fields have changed by comparing current values to initialValues
                        if (
                          formState.values?.answer &&
                          formState.initialValues?.answer
                        ) {
                          const changed = new Set<number>();
                          formState.values.answer.forEach(
                            (val: any, idx: number) => {
                              const initialVal =
                                formState.initialValues?.answer?.[idx];
                              if (
                                JSON.stringify(val) !==
                                JSON.stringify(initialVal)
                              ) {
                                changed.add(idx);
                              }
                            }
                          );
                          setChangedFields(changed);
                        }
                      }}
                    />
                    {responses?.questionStates?.map((item, i) => {
                      const response = responses.questionResponses?.find(
                        (q) => q.questionId === item.questionId
                      );
                      const question = questionnaire.questions?.find(
                        (q: any) => q.id === item.questionId
                      );

                      return (
                        <EditableQuestionnaireItem
                          key={item.questionId}
                          question={question}
                          response={response}
                          questionState={item}
                          index={i}
                          isModified={changedFields.has(i)}
                        />
                      );
                    })}
                  </SColumnContent>
                </SColumn>
              </SComparisonGrid>
              {/* Reason for Amendment */}
              <SReasonSection>
                <MuiTypography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
                  {t('Reason for Amendment')} *
                </MuiTypography>
                <MuiTextField
                  fullWidth
                  multiline
                  rows={4}
                  value={reasonForAmendment}
                  onChange={(e) => setReasonForAmendment(e.target.value)}
                  placeholder={t('Type here')}
                  variant="outlined"
                  required
                />
              </SReasonSection>

              {/* Action Buttons */}
              <SActionButtons>
                <MuiButton
                  variant="outlined"
                  color="primary"
                  onClick={onCancel}
                  disabled={loading}
                >
                  {t('Cancel')}
                </MuiButton>
                <MuiButton
                  variant="contained"
                  color="primary"
                  type="submit"
                  disabled={
                    loading ||
                    changedFields.size === 0 ||
                    !reasonForAmendment.trim()
                  }
                >
                  {loading ? t('Submitting...') : t('Submit')}
                </MuiButton>
              </SActionButtons>
            </form>
          )}
        />
      </SContent>

      {/* Confirmation Dialog */}
      {confirmationDialog.isOpen && (
        <ConfirmationDialog
          open={confirmationDialog.isOpen}
          close={confirmationDialog.close}
          initialData={confirmationDialog.initialData}
        />
      )}
    </>
  );
};

export default QuestionnaireAmendmentStep;

const SHeader = styled(MuiBox)`
  && {
    width: 100%;
    text-align: center;
    padding: 28px 60px 20px;
    border-bottom: 1px solid ${({ theme }) => theme.palette.grey[200]};
  }
`;

const SContent = styled(MuiBox)`
  && {
    padding: 24px 60px;
    overflow-y: auto;
    max-height: calc(100vh - 200px);
  }
`;

const SComparisonGrid = styled(MuiBox)`
  && {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 24px;
    margin-bottom: 24px;

    @media (max-width: 1200px) {
      grid-template-columns: 1fr;
    }
  }
`;

const SColumn = styled(MuiBox)`
  && {
    display: flex;
    flex-direction: column;
  }
`;

const SColumnHeader = styled(MuiBox)`
  && {
    padding: 16px;
    background-color: ${({ theme }) => theme.palette.grey[100]};
    border-radius: 8px 8px 0 0;
    text-align: center;
    border: 1px solid ${({ theme }) => theme.palette.grey[200]};
  }
`;

const SColumnContent = styled(MuiBox)`
  && {
    border: 1px solid ${({ theme }) => theme.palette.grey[200]};
    border-top: none;
    border-radius: 0 0 8px 8px;
    padding: 16px;
    background-color: ${({ theme }) => theme.palette.background.paper};
  }
`;

const SReasonSection = styled(MuiBox)`
  && {
    margin-bottom: 24px;
    padding: 24px;
    background-color: ${({ theme }) => theme.palette.grey[50]};
    border-radius: 8px;
  }
`;

const SActionButtons = styled(MuiBox)`
  && {
    display: flex;
    justify-content: flex-end;
    gap: 16px;
    padding-top: 16px;
    border-top: 1px solid ${({ theme }) => theme.palette.grey[200]};
  }
`;
